import { DevicePid } from '@/define/define'
import { MagnetKeyType } from '@libs/types/enum'
import { reverseMap } from '@libs/utils/tools'

export enum MG63KeyMode {
  NORMAL = 0,
  RT = 1,
  DKS = 2,
  MT = 3,
  TGL = 4,
  SOCD = 5,
  RS = 6,
  END = 7,
  MPT = 8,
}

export const huochairenMagnetModeConvert = (mode: MG63KeyMode) => {
  if (mode == MG63KeyMode.DKS) {
    return MagnetKeyType.DKS
  }
  if (mode == MG63KeyMode.MT) {
    return MagnetKeyType.MT
  }
  if (mode == MG63KeyMode.END) {
    return MagnetKeyType.END
  }
  if (mode == MG63KeyMode.TGL) {
    return MagnetKeyType.TGL
  }
  if (mode == MG63KeyMode.SOCD) {
    return MagnetKeyType.KME
  }
  if (mode == MG63KeyMode.RS) {
    return MagnetKeyType.RS
  }
  if (mode == MG63KeyMode.MPT) {
    return MagnetKeyType.MPT
  }
  return MG63KeyMode.NORMAL
}

export enum MG63TGLMode {
  UNCONFIGURED = 0,
  TOGGLE_ON_PRESS = 1, // 连续触发模式（按一下切换状态）
  HOLD_TO_ACTIVATE = 2, // 长按保持模式
}

export enum MG63SOCDPolicy {
  NEUTRAL = 0, // 中和模式
  LAST_WINS = 1, // 后按下优先
  ABSOLUTE_WINS = 2, // 绝对优先
}

export interface MG63MagKeyInfo {
  releaseCalibration: number // U16
  pressCalibration: number // U16
  pressTriggerPointMm: number // U16, 单位: 0.001mm
  releaseTriggerPointMm: number // U16
  pressTriggerValueAdc: number // U16
  releaseTriggerValueAdc: number // U16
  isRTEnabled: number // U8 (0/1)
  isContinueRTEnabled: number // U8 (0/1)
  rtPressTriggerPointMm: number // U16
  rtReleaseTriggerPointMm: number // U16
  rtPressTriggerDistance: number // U16
  rtReleaseTriggerDistance: number // U16

  keyMode: MG63KeyMode // uint8_t
  topDeadZone: number // uint8_t, 单位: 0.01mm
  bottomDeadZone: number // uint8_t, 单位: 0.01mm

  // DKS 配置
  dksLowTriggerPointMm: number // U16
  dksHighTriggerPointMm: number // U16
  dksLowTrigger: number // U16
  dksHighTrigger: number // U16
  dksKeyValues: number[] // U8[4] → 长度为4的数组
  dksEventConfig: number[] // U8[DSK_EVENT_COUNT]

  // MT 配置
  mtTapKey: number // U8
  mtHoldKey: number // U8
  mtHoldTime: number // U16 (ms)

  // TGL 配置
  tglKey: number // U8
  tglHoldTime: number // U16
  tglMode: MG63TGLMode // TGLMode

  // SOCD 配置
  isSOCDEnabled: boolean // bool → 转为 boolean
  socdPartnerRow: number // uint8_t
  socdPartnerCol: number // uint8_t
  socdPolicy: MG63SOCDPolicy // SOCD_Policy
  socdPriority: number // uint8_t (0=当前键优先, 1=配对键优先)

  // RS 配置
  rsPartnerRow: number // uint8_t
  rsPartnerCol: number // uint8_t

  // END 配置
  endPressKey: number // uint8_t
  endReleaseKey: number // uint8_t
  endDelayTime: number // U16

  // MPT 配置
  mptLowTriggerPointMm: number // U16
  mptMidTriggerPointMm: number // U16
  mptHighTriggerPointMm: number // U16
  mptLowTrigger: number // U16
  mptMidTrigger: number // U16
  mptHighTrigger: number // U16
  mptLowKey: number // U8
  mptMidKey: number // U8
  mptHighKey: number // U8
}

export enum MG63SetCmd {
  SetMT = 0x30,
  SetTGL = 0x31,
  SetSOCD = 0x32,
  SetRS = 0x33,
  SetEnd = 0x34,
  SetMPT = 0x35,
  SetDKS = 0x36,
  SetTravel = 0x40,
  SetRTTravel = 0x41,
  SetDeadZone = 0x42,
  TravelTest = 0x43,
  Calibration = 0x44,
  ReadData = 0xe0,
}

export function decodeMagKeyInfo(buffer: Uint8Array): MG63MagKeyInfo {
  if (buffer.length !== 75) {
    throw new Error(`Invalid buffer size: expected 75 bytes, got ${buffer.length}`)
  }

  const view = new DataView(buffer.buffer, buffer.byteOffset, buffer.byteLength)

  let offset = 0

  // Helper to read U16 (little-endian)
  const readU16 = () => {
    const val = view.getUint16(offset, true) // true = littleEndian
    offset += 2
    return val
  }

  // Helper to read U8
  const readU8 = () => {
    const val = view.getUint8(offset)
    offset += 1
    return val
  }

  // Helper to read boolean (U8 → bool)
  const readBool = () => readU8() !== 0

  // Helper to read fixed-length U8 array
  const readU8Array = (len: number) => {
    const arr = []
    for (let i = 0; i < len; i++) {
      arr.push(readU8())
    }
    return arr
  }

  // === 开始解析 ===
  const releaseCalibration = readU16()
  const pressCalibration = readU16()
  const pressTriggerPointMm = readU16()
  const releaseTriggerPointMm = readU16()
  const pressTriggerValueAdc = readU16()
  const releaseTriggerValueAdc = readU16()

  const isRTEnabled = readU8()
  const isContinueRTEnabled = readU8()
  const rtPressTriggerPointMm = readU16()
  const rtReleaseTriggerPointMm = readU16()
  const rtPressTriggerDistance = readU16()
  const rtReleaseTriggerDistance = readU16()

  const keyMode = readU8() as MG63KeyMode
  const topDeadZone = readU8()
  const bottomDeadZone = readU8()

  // DKS
  const dskLowTriggerPointMm = readU16()
  const dskHighTriggerPointMm = readU16()
  const dskLowTrigger = readU16()
  const dskHighTrigger = readU16()
  const dskKeyValues = readU8Array(4)
  const dskEventConfig = readU8Array(4) // 假设 DSK_EVENT_COUNT = 4

  // MT
  const mtTapKey = readU8()
  const mtHoldKey = readU8()
  const mtHoldTime = readU16()

  // TGL
  const tglKey = readU8()
  const tglHoldTime = readU16()
  const tglMode = readU8() as MG63TGLMode

  // SOCD
  const isSOCDEnabled = readBool()
  const socdPartnerRow = readU8()
  const socdPartnerCol = readU8()
  const socdPolicy = readU8() as MG63SOCDPolicy
  const socdPriority = readU8()

  // RS
  const rsPartnerRow = readU8()
  const rsPartnerCol = readU8()

  // END
  const endPressKey = readU8()
  const endReleaseKey = readU8()
  const endDelayTime = readU16()

  // MPT
  const mptLowTriggerPointMm = readU16()
  const mptMidTriggerPointMm = readU16()
  const mptHighTriggerPointMm = readU16()
  const mptLowTrigger = readU16()
  const mptMidTrigger = readU16()
  const mptHighTrigger = readU16()
  const mptLowKey = readU8()
  const mptMidKey = readU8()
  const mptHighKey = readU8()

  // 验证是否读完 75 字节
  if (offset !== 75) {
    console.warn(`Decode offset mismatch: expected 75, got ${offset}`)
  }

  return {
    releaseCalibration,
    pressCalibration,
    pressTriggerPointMm,
    releaseTriggerPointMm,
    pressTriggerValueAdc,
    releaseTriggerValueAdc,
    isRTEnabled,
    isContinueRTEnabled,
    rtPressTriggerPointMm,
    rtReleaseTriggerPointMm,
    rtPressTriggerDistance,
    rtReleaseTriggerDistance,
    keyMode,
    topDeadZone,
    bottomDeadZone,
    dksLowTriggerPointMm: dskLowTriggerPointMm,
    dksHighTriggerPointMm: dskHighTriggerPointMm,
    dksLowTrigger: dskLowTrigger,
    dksHighTrigger: dskHighTrigger,
    dksKeyValues: dskKeyValues,
    dksEventConfig: dskEventConfig,
    mtTapKey,
    mtHoldKey,
    mtHoldTime,
    tglKey,
    tglHoldTime,
    tglMode,
    isSOCDEnabled,
    socdPartnerRow,
    socdPartnerCol,
    socdPolicy,
    socdPriority,
    rsPartnerRow,
    rsPartnerCol,
    endPressKey,
    endReleaseKey,
    endDelayTime,
    mptLowTriggerPointMm,
    mptMidTriggerPointMm,
    mptHighTriggerPointMm,
    mptLowTrigger,
    mptMidTrigger,
    mptHighTrigger,
    mptLowKey,
    mptMidKey,
    mptHighKey,
  }
}

const MG63MagMap: { [index: number]: number } = {
  0x00: 0x29,
  0x05: 0x1e,
  0x0a: 0x1f,
  0x0f: 0x20,
  0x14: 0x21,
  0x19: 0x22,
  0x1e: 0x23,
  0x23: 0x24,
  0x28: 0x25,
  0x2d: 0x26,
  0x32: 0x27,
  0x37: 0x2d,
  0x3c: 0x2e,
  0x41: 0x2a, //第一行
  // 0x46: 0x49,

  0x01: 0x2b,
  0x06: 0x14,
  0x0b: 0x1a,
  0x10: 0x8,
  0x15: 0x15,
  0x1a: 0x17,
  0x1f: 0x1c,
  0x24: 0x18,
  0x29: 0x0c,
  0x2e: 0x12,
  0x33: 0x13,
  0x38: 0x2f,
  0x3d: 0x30,
  0x42: 0x31,

  0x02: 0x39,
  0x07: 0x04,
  0x0c: 0x16,
  0x11: 0x07,
  0x16: 0x09,
  0x1b: 0x0a,
  0x20: 0x0b,
  0x25: 0x0d,
  0x2a: 0x0e,
  0x2f: 0x0f,
  0x34: 0x33,
  0x39: 0x34,
  0x3e: 0x28, // 第三行
  // 0x43: 0x4b,

  0x03: 0xe1,
  0x08: 0x1d,
  0x0d: 0x1b,
  0x12: 0x06,
  0x17: 0x19,
  0x1c: 0x05,
  0x21: 0x11,
  0x26: 0x10,
  0x2b: 0x36,
  0x30: 0x37,
  0x35: 0x38,
  0x3a: 0xe5,
  0x3f: 0x52,
  0x44: 0x4c, //第四行

  0x04: 0xe0,
  0x09: 0xe3,
  0x0e: 0xe2,
  0x13: 0x2c,
  0x18: 0xe6,
  0x1d: 0xff,
  0x22: 0x50,
  0x27: 0x51,
  0x2c: 0x4f,
}

// 写一个方法返回反转MG63MagMap的键值对
const mg63MagMapReverse = reverseMap(MG63MagMap)
// KK=>kk
const RA68MagMap: { [index: number]: number } = {
  0x00: 0x29,
  0x05: 0x1e,
  0x0a: 0x1f,
  0x0f: 0x20,
  0x14: 0x21,
  0x19: 0x22,
  0x1e: 0x23,
  0x23: 0x24,
  0x28: 0x25,
  0x2d: 0x26,
  0x32: 0x27,
  0x37: 0x2d,
  0x3c: 0x2e,
  0x41: 0x2a,
  0x46: 0x49, //第一行

  0x01: 0x2b,
  0x06: 0x14,
  0x0b: 0x1a,
  0x10: 0x8,
  0x15: 0x15,
  0x1a: 0x17,
  0x1f: 0x1c,
  0x24: 0x18,
  0x29: 0x0c,
  0x2e: 0x12,
  0x33: 0x13,
  0x38: 0x2f,
  0x3d: 0x30,
  0x42: 0x31,
  0x47: 0x4c,

  0x02: 0x39,
  0x07: 0x04,
  0x0c: 0x16,
  0x11: 0x07,
  0x16: 0x09,
  0x1b: 0x0a,
  0x20: 0x0b,
  0x25: 0x0d,
  0x2a: 0x0e,
  0x2f: 0x0f,
  0x34: 0x33,
  0x39: 0x34,
  0x3e: 0x28,
  0x43: 0x4b, // 第三行

  0x03: 0xe1,
  0x08: 0x1d,
  0x0d: 0x1b,
  0x12: 0x06,
  0x17: 0x19,
  0x1c: 0x05,
  0x21: 0x11,
  0x26: 0x10,
  0x2b: 0x36,
  0x30: 0x37,
  0x35: 0x38,
  0x3a: 0xe5,
  0x3f: 0x52,
  0x44: 0x4e, //第四行

  0x04: 0xe0,
  0x09: 0xe3,
  0x0e: 0xe2,
  0x13: 0x2c,
  0x18: 0xe6,
  0x1d: 0xff,
  0x22: 0xe4,
  0x27: 0x50,
  0x2c: 0x51,
  0x31: 0x4f,
}

const mg75v2MagMap: { [index: number]: number } = {
  /* Row 0: Index = Col * 6 + 0 */
  0x00: 0x29 /* Esc */,
  0x06: 0x3a /* F1 */,
  0x0c: 0x3b /* F2 */,
  0x12: 0x3c /* F3 */,
  0x18: 0x3d /* F4 */,
  0x1e: 0x3e /* F5 */,
  0x24: 0x3f /* F6 */,
  0x2a: 0x40 /* F7 */,
  0x30: 0x41 /* F8 */,
  0x36: 0x42 /* F9 */,
  0x3c: 0x43 /* F10 */,
  0x42: 0x44 /* F11 */,
  0x48: 0x45 /* F12 */,
  0x4e: 0x46 /* PrtSc/Print */,
  0x54: 0x4c /* Del */,

  /* Row 1: Index = Col * 6 + 1 */
  0x01: 0x35 /* ~ */,
  0x07: 0x1e /* 1 */,
  0x0d: 0x1f /* 2 */,
  0x13: 0x20 /* 3 */,
  0x19: 0x21 /* 4 */,
  0x1f: 0x22 /* 5 */,
  0x25: 0x23 /* 6 */,
  0x2b: 0x24 /* 7 */,
  0x31: 0x25 /* 8 */,
  0x37: 0x26 /* 9 */,
  0x3d: 0x27 /* 0 */,
  0x43: 0x2d /* - */,
  0x49: 0x2e /* = */,
  0x4f: 0x2a /* Backspace */,
  0x55: 0x49 /* Ins */,

  /* Row 2: Index = Col * 6 + 2 */
  0x02: 0x2b /* Tab */,
  0x08: 0x14 /* Q */,
  0x0e: 0x1a /* W */,
  0x14: 0x08 /* E */,
  0x1a: 0x15 /* R */,
  0x20: 0x17 /* T */,
  0x26: 0x1c /* Y */,
  0x2c: 0x18 /* U */,
  0x32: 0x0c /* I */,
  0x38: 0x12 /* O */,
  0x3e: 0x13 /* P */,
  0x44: 0x2f /* [ */,
  0x4a: 0x30 /* ] */,
  0x50: 0x31 /* \ */,
  0x56: 0x4b /* PgUp */,

  /* Row 3: Index = Col * 6 + 3 */
  0x03: 0x39 /* Caps */,
  0x09: 0x04 /* A */,
  0x0f: 0x16 /* S */,
  0x15: 0x07 /* D */,
  0x1b: 0x09 /* F */,
  0x21: 0x0a /* G */,
  0x27: 0x0b /* H */,
  0x2d: 0x0d /* J */,
  0x33: 0x0e /* K */,
  0x39: 0x0f /* L */,
  0x3f: 0x33 /* ; */,
  0x45: 0x34 /* ' */,
  0x4b: 0x28 /* Enter */,
  0x51: 0x4e /* PgDn */,

  /* Row 4: Index = Col * 6 + 4 */
  0x04: 0xe1 /* LShift */,
  0x0a: 0x1d /* Z */,
  0x10: 0x1b /* X */,
  0x16: 0x06 /* C */,
  0x1c: 0x19 /* V */,
  0x22: 0x05 /* B */,
  0x28: 0x11 /* N */,
  0x2e: 0x10 /* M */,
  0x34: 0x36 /* , */,
  0x3a: 0x37 /* . */,
  0x40: 0x38 /* / */,
  0x46: 0xe5 /* RShift */,
  0x4c: 0x52 /* Up */,

  /* Row 5: Index = Col * 6 + 5 */
  0x05: 0xe0 /* LCtrl */,
  0x0b: 0xe3 /* LWin */,
  0x11: 0xe2 /* LAlt */,
  0x17: 0x2c /* Space */,
  0x1d: 0xe6 /* RAlt */,
  0x23: 0xff /* Fn */,
  0x29: 0x50 /* Left */,
  0x2f: 0x51 /* Down */,
  0x35: 0x4f /* Right */,
}

const ra68MagMapReverse = reverseMap(RA68MagMap)

export const huochairenMagMapReverse = (deviceID: DevicePid) => {
  if (deviceID == DevicePid.IROKRA68_1) {
    return ra68MagMapReverse
  }
  if (deviceID == DevicePid.IROKMG75V2C12) {
    return reverseMap(mg75v2MagMap)
  }
  return mg63MagMapReverse
}

export const mg63ReportMap = new Map<number, number>([
  [1, 8000],
  [2, 4000],
  [3, 2000],
  [4, 1000],
  [5, 500],
  [6, 250],
  [7, 125],
])
// #define ConfigNum_OFFSET 1
// #define ReportRate_OFFSET 16
// #define System_OFFSET 17
// #define CustomLedEnable_OFFSET 18
// #define MainLedMode_OFFSET 19
// #define CustomLedMode_OFFSET 20
// #define LogoLedMode_OFFSET 21
// #define SideLedMode_OFFSET 22
// #define Winlock_OFFSET 22
// #define KBlock_OFFSET 23
// #define FirstSleepTime_OFFSET 33
// #define SecondSleepTime_OFFSET 34
export enum MG63KeyboardParams {
  ConfigNum_OFFSET = 1,
  ReportRate_OFFSET = 16,
  System_OFFSET = 17,
  CustomLedEnable_OFFSET = 18,
  MainLedMode_OFFSET = 19,
  CustomLedMode_OFFSET = 20,
  LogoLedMode_OFFSET = 21,
  SideLedMode_OFFSET = 22,
  Winlock_OFFSET = 23,
  KBlock_OFFSET = 24,
  DirectionKeySwap = 25,
  KeyMode = 26,
  FirstSleepTime_OFFSET = 33,
  SecondSleepTime_OFFSET = 34,
  CancelDefaultDeadZone = 40,
}

export const MG63CustomMap: { [index: number]: number } = {
  0x18: 0x1a,
  0x27: 0x04,
  0x2a: 0x16,
}
